import { Cloud, Clock, ListChecks, Award, TrendingUp, ArrowRight, PlayCircle, CheckCircle2, XCircle, History } from "lucide-react";
import { Button } from "@/components/ui/button";

const MODE_META = {
  quick: { icon: ListChecks, blurb: "A fast warm-up to check your knowledge." },
  practice: { icon: TrendingUp, blurb: "A focused practice run across all domains." },
  full: { icon: Award, blurb: "The full 40-question, 45-minute exam simulation with certificate." },
};

const READINESS_STYLE = {
  "Exam Ready": "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  Developing: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
  "Not Ready": "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30",
};

function fmtDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export default function Az900Dashboard({ meta, history, resumeExam, startExam, starting, openResult }) {
  if (!meta) return <div className="p-10 text-muted-foreground" data-testid="az900-loading">Loading AZ-900 simulator…</div>;
  const bp = meta.blueprint;
  const modes = meta.modes;

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="az900-dashboard">
      {/* Header */}
      <div className="rounded-2xl border border-[#0078d4]/25 bg-gradient-to-br from-[#0078d4]/10 via-transparent to-transparent p-7">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#0078d4] grid place-items-center text-white shadow-lg shadow-[#0078d4]/30">
            <Cloud className="h-6 w-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0078d4]">Exam Simulator</div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight leading-none mt-0.5">
              {bp.exam_code} · {bp.exam_name}
            </h1>
          </div>
        </div>
        <p className="text-muted-foreground mt-3 max-w-3xl text-sm leading-relaxed">
          A realistic Microsoft Azure Fundamentals exam experience. Questions are original, objective-aligned content
          (blueprint {bp.skills_measured_version}). Simulated score is on a <span className="font-semibold text-foreground">0–1000 scale</span> with a
          benchmark of <span className="font-semibold text-foreground">{bp.benchmark}</span>. This is not a Microsoft certification.
        </p>

        <div className="mt-5 flex flex-wrap gap-6">
          {bp.domains.map((d) => (
            <div key={d.id} data-testid={`az900-domain-weight-${d.id}`} className="min-w-[180px]">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-medium text-foreground">{d.name}</span>
                <span className="text-xs font-mono text-[#0078d4]">{Math.round(d.weight * 100)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted mt-1.5 overflow-hidden">
                <div className="h-full bg-[#0078d4] rounded-full" style={{ width: `${d.weight * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-muted-foreground">Question bank: {meta.bank_size} original questions</div>
      </div>

      {/* Resume banner */}
      {resumeExam && (
        <div className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center justify-between gap-4" data-testid="az900-resume-banner">
          <div className="flex items-center gap-3">
            <PlayCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <div className="font-semibold">You have an exam in progress</div>
              <div className="text-muted-foreground text-xs">Resume before the timer runs out.</div>
            </div>
          </div>
          <Button onClick={resumeExam} data-testid="az900-resume-btn" className="bg-[#0078d4] hover:bg-[#106ebe] gap-2">
            Resume exam <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Mode cards */}
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        {Object.entries(modes).map(([key, m]) => {
          const Icon = MODE_META[key]?.icon || ListChecks;
          return (
            <div key={key} data-testid={`az900-mode-${key}`} className="rounded-xl border border-border bg-card p-5 flex flex-col hover:border-[#0078d4]/50 hover:-translate-y-0.5 transition-[transform,border-color]">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-md bg-[#0078d4]/10 grid place-items-center text-[#0078d4]">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight">{m.label}</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-2 flex-1">{MODE_META[key]?.blurb}</p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-4">
                <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {m.count} questions</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {m.minutes} min</span>
                {key === "full" && <span className="flex items-center gap-1 text-[#0078d4]"><Award className="h-3.5 w-3.5" /> Certificate</span>}
              </div>
              <Button
                className="w-full mt-4 gap-2 bg-[#0078d4] hover:bg-[#106ebe]"
                disabled={starting || !!resumeExam}
                data-testid={`az900-start-${key}`}
                onClick={() => startExam(key)}
              >
                {resumeExam ? "Finish current exam first" : <>Start <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          );
        })}
      </div>

      {/* History */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-[#0078d4]" />
            <h2 className="font-display font-bold text-xl tracking-tight">Your attempts</h2>
          </div>
          {history?.readiness && (
            <span data-testid="az900-readiness" className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${READINESS_STYLE[history.readiness] || ""}`}>
              {history.readiness}
            </span>
          )}
        </div>

        {(!history || history.attempts.length === 0) ? (
          <div className="mt-4 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="az900-no-attempts">
            No attempts yet. Start with a Quick Quiz to gauge where you stand.
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm" data-testid="az900-history-table">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Mode</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3 font-medium">Correct</th>
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Result</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {[...history.attempts].reverse().map((a) => (
                  <tr key={a.attempt_id} className="border-t border-border" data-testid={`az900-attempt-${a.attempt_id}`}>
                    <td className="px-4 py-3 text-muted-foreground">{(a.submitted_at || "").slice(0, 10) || "—"}</td>
                    <td className="px-4 py-3 capitalize">{modes[a.mode]?.label || a.mode}</td>
                    <td className="px-4 py-3 font-mono font-semibold">{a.scaled_score}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.correct}/{a.total}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDuration(a.duration_sec)}</td>
                    <td className="px-4 py-3">
                      {a.passed ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium"><CheckCircle2 className="h-4 w-4" /> Pass</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium"><XCircle className="h-4 w-4" /> Below benchmark</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" className="text-[#0078d4] gap-1" data-testid={`az900-view-${a.attempt_id}`} onClick={() => openResult(a.attempt_id)}>
                        Review <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
